// Portal Types - Multi-Tenant Business Users

// ============================================
// User Roles
// ============================================

export type BusinessRole = 'PFENT' | 'PFLOJ' | 'PFEMP' | 'PFGOV' | 'PFONG' | 'PFEVT' | 'PFPRO';
export type UserRole = 'PFADM' | 'PFCLI' | 'PFCON' | 'PFOPE' | 'PFAGE' | BusinessRole;

export const BUSINESS_ROLES: BusinessRole[] = ['PFENT', 'PFLOJ', 'PFEMP', 'PFGOV', 'PFONG', 'PFEVT', 'PFPRO'];

export const ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  PFADM: 'Administrador',
  PFCLI: 'Cliente',
  PFENT: 'Entidade',
  PFCON: 'Consultor',
  PFOPE: 'Operador',
  PFAGE: 'Agente',
  PFLOJ: 'Loja/Comércio',
  PFEMP: 'Empresa',
  PFGOV: 'Governo',
  PFONG: 'ONG/Associação',
  PFEVT: 'Promotor de Eventos',
  PFPRO: 'Profissional Liberal',
};

// ============================================
// Portal User Info
// ============================================

export interface PortalUserInfo {
  uid: string;
  email?: string;
  role: UserRole;
  roleDescription: string;
  mysqlId: string;
  entidadeId?: string;
  permissions: string[];
  isBusinessUser: boolean;
}

export interface PortalEntidade {
  ID: string;
  CODIGO: string;
  DESIG: string;
  MORADA?: string;
  NUM_CONTA?: string;
  TIPO_ENTIDADE?: string;
}

// ============================================
// Dashboard
// ============================================

export interface PortalDashboardStats {
  role: UserRole;
  roleDescription: string;
  entidadeId?: string;
  entidade?: PortalEntidade;
  pagamentos?: {
    totalTransacoes: number;
    totalRecebido: number;
    totalPendente: number;
  };
  loja?: {
    totalProdutos: number;
    pedidosMes: number;
  };
  eventos?: {
    totalEventos: number;
    eventosAtivos: number;
  };
  doacoes?: {
    totalCampanhas: number;
    totalArrecadado: number;
  };
  atividadeRecente: PortalAtividade[];
}

export interface PortalAtividade {
  tipo: string;
  referencia: string;
  valor: number;
  estado: string;
  data: string;
}

// ============================================
// Products (PFLOJ)
// ============================================

export interface PortalProduct {
  ID_PRODUTO: string;
  NOME: string;
  DESCRICAO?: string;
  PRECO: number;
  PRECO_PROMOCIONAL?: number;
  IMAGEM?: string;
  MIME_TYPE?: string;
  CATEGORIA_ID?: string;
  CATEGORIA_NOME?: string;
  ID_LOJA?: string;
  STOCK: number;
  ATIVO: boolean;
}

export interface PortalProductCategory {
  ID: string;
  NOME: string;
  DESCRICAO?: string;
  LOJA_ID?: string;
  totalProdutos?: number;
}

// ============================================
// Services (PFEMP, PFGOV, PFPRO)
// ============================================

export interface PortalService {
  ENTIDADE_SERVICO_ID: string;
  SERVICO_ID: string;
  SERVICO_ESTADO: string;
  DESIG: string;
  DESCR?: string;
  CODIGO?: string;
  ICONE?: string;
  LOGO?: string;
  MIME_TYPE?: string;
  CATEGORIA?: string;
  CATEGORIA_ID?: string;
  TIPO_SERVICO?: string;
}

// ============================================
// Payments
// ============================================

export interface PortalPayment {
  ID: string;
  REFERENCIA: string;
  MONTANTE: number;
  ESTADO: 'N' | 'S' | 'F' | 'E' | 'P';
  DT_REGISTO: string;
  NUMERO_AUTORIZACAO?: string;
  DESCRICAO?: string;
  CLIENTE_NOME?: string;
  CLIENTE_EMAIL?: string;
  SERVICO?: string;
  ENTIDADE_SERVICO_ID?: string;
  estadoDescricao?: string;
  estadoCor?: string;
}

export interface PortalPaymentStats {
  total: number;
  totalRecebido: number;
  totalPendente: number;
  pagos: number;
  pendentes: number;
  falhados: number;
}

// ============================================
// Events (PFEVT)
// ============================================

export interface PortalEvent {
  ID: string;
  ENTIDADE_ID: string;
  NOME: string;
  DESCRICAO?: string;
  DATA_INICIO: string;
  DATA_FIM?: string;
  LOCAL?: string;
  ENDERECO?: string;
  CAPACIDADE?: number;
  IMAGEM?: string;
  MIME_TYPE?: string;
  ESTADO: 'A' | 'I' | 'C';
  ESGOTADO?: boolean;
  totalCapacidade?: number;
  totalBilhetes?: number;
  bilhetesUsados?: number;
}

export interface PortalTicket {
  ID: string;
  EVENTO_ID: string;
  CLIENTE_ID?: string;
  CLIENTE_NOME?: string;
  CLIENTE_EMAIL?: string;
  TIPO_BILHETE_ID?: string;
  TIPO_BILHETE?: string;
  CODIGO: string;
  PRECO: number;
  ESTADO: 'ATIVO' | 'USADO' | 'EXPIRADO' | 'CANCELADO';
  DATA_COMPRA: string;
  DATA_USO?: string;
}

export interface PortalTicketType {
  ID: string;
  EVENTO_ID: string;
  NOME: string;
  DESCRICAO?: string;
  PRECO: number;
  QUANTIDADE?: number;
}

// ============================================
// Donations (PFONG)
// ============================================

export interface PortalCampaign {
  ID: string;
  ENTIDADE_ID: string;
  TITULO: string;
  DESCRICAO?: string;
  META: number;
  DATA_INICIO?: string;
  DATA_FIM?: string;
  IMAGEM?: string;
  MIME_TYPE?: string;
  CATEGORIA?: string;
  ESTADO: 'ATIVA' | 'PAUSADA' | 'CONCLUIDA' | 'CANCELADA';
  DATA_CRIACAO: string;
  totalArrecadado?: number;
  totalDoadores?: number;
  percentagemMeta?: number;
}

export interface PortalDonation {
  ID: string;
  CAMPANHA_ID: string;
  CLIENTE_ID?: string;
  DOADOR_NOME?: string;
  DOADOR_EMAIL?: string;
  VALOR: number;
  MENSAGEM?: string;
  ANONIMO: boolean;
  ESTADO: 'PENDENTE' | 'CONFIRMADO' | 'CANCELADO';
  DATA_DONATIVO: string;
  RECOMPENSA?: string;
}

export interface PortalReward {
  ID: string;
  CAMPANHA_ID: string;
  TITULO: string;
  DESCRICAO?: string;
  VALOR_MINIMO: number;
  QUANTIDADE_DISPONIVEL?: number;
}

// ============================================
// Orders (PFLOJ)
// ============================================

export interface PortalOrder {
  ID: string;
  CLIENTE_ID?: string;
  CLIENTE_NOME?: string;
  ID_LOJA: string;
  DATA_CRIACAO: string;
  TOTAL: number;
  ESTADO: 'PENDENTE' | 'CONFIRMADO' | 'EM_PREPARACAO' | 'ENVIADO' | 'ENTREGUE' | 'CANCELADO';
  ITEMS?: string;
  ENDERECO_ENTREGA?: string;
  OBSERVACOES?: string;
}

// ============================================
// Reports
// ============================================

export interface PortalReportSummary {
  entidade: PortalEntidade;
  periodo: {
    dias: number;
    dataInicio?: string;
    dataFim?: string;
  };
  financeiro: {
    totalTransacoes: number;
    totalRecebido: number;
    totalFalhado: number;
    totalEstornado: number;
    ticketMedio: number;
    clientesUnicos: number;
  };
  porEstado: Array<{
    ESTADO: string;
    estadoDescricao: string;
    total: number;
    valor: number;
  }>;
  porServico: Array<{
    servico: string;
    transacoes: number;
    valor: number;
  }>;
}

// ============================================
// Business Registration
// ============================================

export interface BusinessRegistrationData {
  tipoPerfil: BusinessRole;
  nomeNegocio: string;
  nif?: string;
  email: string;
  telefone?: string;
  morada?: string;
  cidade?: string;
  paisId?: number;
  descricao?: string;
  website?: string;
  username: string;
  password: string;
  nomeCompleto?: string;
}

export interface BusinessRegistration {
  ID: string;
  USER_ID?: string;
  TIPO_PERFIL: BusinessRole;
  NOME_NEGOCIO: string;
  NIF?: string;
  EMAIL: string;
  TELEFONE?: string;
  MORADA?: string;
  CIDADE?: string;
  DESCRICAO?: string;
  WEBSITE?: string;
  ESTADO: 'PENDENTE' | 'EM_REVISAO' | 'APROVADO' | 'REJEITADO' | 'SUSPENSO';
  DT_REGISTO: string;
  DT_APROVACAO?: string;
}

// ============================================
// API Responses
// ============================================

export interface PortalApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  stats?: Record<string, any>;
}
